import { useCallback, useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { useAuthStore } from '../stores/auth';
import { api } from '../lib/api';

const RECENTS_LIMIT = 6;
const FAVORITES_LIMIT = 50;

function recentsKey(userId: string | undefined): string {
  return `nav-recents:${userId ?? 'anon'}`;
}

function readRecents(userId: string | undefined): string[] {
  try {
    const raw = localStorage.getItem(recentsKey(userId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((p) => typeof p === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Verwaltet persönliche Navigations-Einstellungen:
 * - Favoriten: serverseitig am Nutzerprofil (geräteübergreifend)
 * - Verlauf: lokal pro Gerät (ephemer)
 */
export function useNavPreferences() {
  const { user, setUser } = useAuthStore();
  const favorites = (user?.uiPreferences?.favorites ?? []).filter((p) => typeof p === 'string');

  const [recents, setRecents] = useState<string[]>(() => readRecents(user?.id));

  // Bei Nutzerwechsel Verlauf neu laden
  useEffect(() => {
    setRecents(readRecents(user?.id));
  }, [user?.id]);

  const isFavorite = useCallback(
    (path: string) => favorites.includes(path),
    [favorites]
  );

  const toggleFavorite = useCallback(
    async (path: string) => {
      const current = useAuthStore.getState().user;
      if (!current) return;

      const existing = (current.uiPreferences?.favorites ?? []).filter(
        (p) => typeof p === 'string'
      );
      const next = existing.includes(path)
        ? existing.filter((p) => p !== path)
        : [...existing, path].slice(-FAVORITES_LIMIT);

      const nextPrefs = { ...(current.uiPreferences ?? {}), favorites: next };

      // Optimistisch aktualisieren
      setUser({ ...current, uiPreferences: nextPrefs });

      try {
        const { user: saved } = await api.updateMyPreferences(nextPrefs);
        setUser(saved);
      } catch {
        // Bei Fehler zurückrollen
        setUser(current);
        notifications.show({
          color: 'red',
          title: 'Favorit nicht gespeichert',
          message: 'Bitte später erneut versuchen.',
        });
      }
    },
    [setUser]
  );

  const recordRecent = useCallback(
    (path: string) => {
      const uid = useAuthStore.getState().user?.id;
      setRecents((prev) => {
        const next = [path, ...prev.filter((p) => p !== path)].slice(0, RECENTS_LIMIT);
        try {
          localStorage.setItem(recentsKey(uid), JSON.stringify(next));
        } catch {
          // localStorage nicht verfügbar – ignorieren
        }
        return next;
      });
    },
    []
  );

  return { favorites, isFavorite, toggleFavorite, recents, recordRecent };
}
